/**
 * Resilient Lazy Import Utility
 * 
 * Handles chunk loading errors gracefully by:
 * 1. Detecting "Failed to fetch dynamically imported module" errors
 * 2. Auto-retrying with exponential backoff
 * 3. Forcing page reload after max retries
 * 
 * @see PERFORMANCE_REPORT.md for lazy loading strategy
 */

import { lazy, ComponentType } from 'react';
import * as Sentry from '@sentry/react';
import { logger } from '@/lib/logger';

const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff

/**
 * Checks if an error is a chunk loading error
 */
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  
  const chunkErrorPatterns = [
    'Failed to fetch dynamically imported module',
    'Loading chunk',
    'ChunkLoadError',
    'Loading CSS chunk',
    'Unable to preload CSS',
  ];
  
  return chunkErrorPatterns.some(pattern => 
    error.message.includes(pattern) || error.name.includes(pattern)
  );
}

/**
 * Sleep utility for retry delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Resilient lazy loader that handles chunk errors gracefully
 * 
 * @param importFn - Dynamic import function
 * @param componentName - Name for logging (optional)
 * @returns React.lazy component with error handling
 */
export function resilientLazy<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
  componentName = 'UnknownComponent'
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        // If not first attempt, wait before retrying
        if (attempt > 0) {
          const delay = RETRY_DELAYS[attempt - 1] || RETRY_DELAYS[RETRY_DELAYS.length - 1];
          logger.warn(`[resilientLazy] Retry ${attempt}/${MAX_RETRIES} for ${componentName} in ${delay}ms`);
          await sleep(delay);
        }
        
        return await importFn();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        
        if (isChunkLoadError(error)) {
          logger.warn(`[resilientLazy] Chunk error for ${componentName}:`, lastError.message);
          
          // Report to Sentry on first error
          if (attempt === 0) {
            Sentry.captureException(lastError, {
              tags: {
                type: 'chunk_load_error',
                component: componentName,
              },
              extra: {
                attempt,
                willRetry: attempt < MAX_RETRIES,
              },
            });
          }
          
          // After max retries, force reload
          if (attempt >= MAX_RETRIES) {
            logger.warn(`[resilientLazy] Max retries reached for ${componentName}, forcing reload`);
            
            // Set flag to prevent infinite reload loops
            const reloadKey = `chunk_reload_${componentName}`;
            const lastReload = sessionStorage.getItem(reloadKey);
            const now = Date.now();
            
            // Only reload if we haven't reloaded in the last 30 seconds
            if (!lastReload || (now - parseInt(lastReload, 10)) > 30000) {
              sessionStorage.setItem(reloadKey, now.toString());
              window.location.reload();
              // Return empty component while reloading
              return { default: (() => null) as unknown as T };
            } else {
              // Already tried reloading recently, throw original error
              logger.error(`[resilientLazy] Already reloaded recently, showing error`);
              throw lastError;
            }
          }
        } else {
          // Not a chunk error, throw immediately
          throw error;
        }
      }
    }
    
    // Should never reach here, but satisfy TypeScript
    throw lastError || new Error('Unknown import error');
  });
}

/**
 * Named export helper for components that use named exports
 * 
 * @example
 * const MyComponent = resilientLazyNamed(
 *   () => import('./MyComponent'),
 *   'MyComponent'
 * );
 */
export function resilientLazyNamed<T extends ComponentType<unknown>>(
  importFn: () => Promise<Record<string, T>>,
  exportName: string
): React.LazyExoticComponent<T> {
  return resilientLazy(
    () => importFn().then(module => ({ default: module[exportName] })),
    exportName
  );
}
